import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Trash2,
  Eye,
  FileText,
  Pencil,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { API } from "./Sidebar";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default: most-recently-modified first
  const [sortBy, setSortBy] = useState("updated_at"); // "name" | "template" | "updated_at"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success("Project deleted");
      fetchProjects();
    } catch (e) {
      toast.error("Failed to delete project");
    }
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      // Date defaults to desc (newest first); text columns default to asc.
      setSortDir(column === "updated_at" ? "desc" : "asc");
    }
  };

  const sortedProjects = useMemo(() => {
    const arr = [...projects];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let va;
      let vb;
      if (sortBy === "updated_at") {
        va = new Date(a.updated_at || a.created_at || 0).getTime();
        vb = new Date(b.updated_at || b.created_at || 0).getTime();
        return (va - vb) * dir;
      }
      if (sortBy === "template") {
        va = (a.template || "").toLowerCase();
        vb = (b.template || "").toLowerCase();
      } else {
        // name
        va = (a.name || "").toLowerCase();
        vb = (b.name || "").toLowerCase();
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [projects, sortBy, sortDir]);

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="w-3.5 h-3.5 opacity-40 ml-1 inline" />;
    }
    return sortDir === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5 ml-1 inline text-orange-500" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 ml-1 inline text-orange-500" />
    );
  };

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            My Projects
          </h2>
          <p className="text-slate-600 mt-1">
            {projects.length} {projects.length === 1 ? "project" : "projects"}
          </p>
        </div>
        <Link to="/admin/builder">
          <Button className="bg-orange-500 hover:bg-orange-600" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      {loading ? (
        <p className="text-center text-slate-500 py-8">Loading...</p>
      ) : projects.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Projects Yet</h3>
            <p className="text-slate-500 mb-4">Create your first lead generation website</p>
            <Link to="/admin/builder">
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="projects-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("name")}
                      className="hover:text-slate-900 transition-colors"
                      data-testid="sort-by-name"
                    >
                      Project name
                      <SortIcon column="name" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("template")}
                      className="hover:text-slate-900 transition-colors"
                      data-testid="sort-by-template"
                    >
                      Template
                      <SortIcon column="template" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("updated_at")}
                      className="hover:text-slate-900 transition-colors"
                      data-testid="sort-by-updated"
                    >
                      Last modified
                      <SortIcon column="updated_at" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => (
                  <tr
                    key={project.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors"
                    data-testid={`project-row-${project.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">
                          {project.name || "Untitled Draft"}
                        </span>
                        {project.status === "draft" && (
                          <Badge
                            variant="secondary"
                            className="bg-amber-100 text-amber-700 border-amber-200"
                            data-testid={`draft-badge-${project.id}`}
                          >
                            Draft
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="capitalize font-normal">
                        {project.template || "—"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {formatDate(project.updated_at || project.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {project.status === "draft" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              navigate(`/admin/builder?draft=${project.id}`)
                            }
                            data-testid={`continue-editing-${project.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Continue
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/projects/${project.id}`)}
                            data-testid={`preview-project-${project.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Preview
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteProject(project.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
